/** @module csi/page/SearchManager */
define([
	"dojo/_base/lang",
	"dojo/store/Memory",
	"underscore",
	"csi",
	"csi/breadcrumb/StatusRenderer",
	"csi/config/preferences",
	"csi/def",
	"csi/def/Heading",
	"csi/heading/NodeImage",
	"csi/def/SearchType",
	"csi/locale",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/state/Cache",
	"csi/service/def/relationshipService",
	"csi/store/_base",
	"csi/store/Names",
	"csi/store/Values",
	"csi/utility/pathUtility",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/Search"
], function(lang, Memory, _, csi, StatusRenderer, configPreferences, def, Heading, NodeImage, SearchType, locale, reflection, reflectionHints,
	{rootCache}, {processSorts}, csiStore, Names, Values, {pathCreate}, xml, Query, Search) {

	// Object: query to use to get active search types from store
	var _activeStoreQuery = {active: true};

	// Store: hash table of global search types
	var _typeStore = undefined;

	// Object: map of search headings per type
	var _mapTypeHeadings = {};

	function _getTypeHeadings(/*String*/type) {
		if (_mapTypeHeadings[type] === undefined) {
			var card = def.getSearchCard(type);
			var headings = null;

			if (card && card.m_headings) {
				// remove any headings already used in display format
				var df = locale.getDisplayFormat(type);
				var a1 = xml.ATTR_NAME;
				var a2 = df ? ((df.a1 == a1) ? df.a2 : df.a1) : null;

				headings = _.filter(card.m_headings, function(h) {
					return h.m_path ? true : (!h.m_id || ((h.m_id != a1) && (h.m_id != a2)));
				});

				// resolve headings - do not allow them to augment Node-level headings as only certain properties are supported for search headings
				_.each(headings, function(h) {
					h.m_bAugmentable = false;
					h.m_class = "csi-card-configured-info";
					_resolveTypeHeading(type, h);
				});
			}

			_mapTypeHeadings[type] = headings;
		}

		return _mapTypeHeadings[type];
	}

	function _resolveTypeHeading(/*String*/type, /*Heading*/h) {
		var rcRoot = reflection.getClass(type);
		var rc = rcRoot.getPathClass(h.m_path);
		h.resolveHeading(locale.localeResolver, null, rc);
	}

	/**
	 * Return store to search for nodes of the same type as the given node
	 * @param {C8Node} node 
	 * @param {boolean} filterBySubtype
	 * @return {Names}
	 */
	function getGlobalStore(node, filterBySubtype) {
		const type = node.$Type;
		const search = csiStore.searchByType(type);
		const q = search.getQuery();

		if (reflectionHints.isTemplateNode(node)) {
			// handle the case that the node is template
			reflectionHints.addQueryConditionsForTemplate(type, q);
		} else {
			getSearchTypeQuery(type, q);

			// search for all nodes of this type that have the same parent (some could be non-empty, such as Country)
			if (reflection.getRelation(node, xml.ATTR_PARENT)) {
				q.RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, node.__Parent__);
			}
		}

		if (filterBySubtype) {
			console.assert(xml.isValidURL(node.Subtype));
			q.RefAttr(reflection.ATTR_SUBTYPE, xml.OP_EQUALS, node.Subtype);
		}

		// handle case where sorting is define for global search
		const sortQuery = getSearchTypeSortQuery(type, true);
		if (sortQuery) {
			q.importQuery(sortQuery);
		}

		return new Names({
			bSort: !sortQuery,
			data: [csiStore.nameObject(node, null, rootCache)],
			request: search.getRequest(),
			typeNode: type
		});
	}

	/**
	 * Return store to search for nodes along a given path from the given node
	 * @param {C8Node} node
	 * @param {string} path 
	 * @param {string} [destinationType] - type of node at end of the path; could be abstract or unknown
	 * @param {Query} [query] - optional additional query conditions
	 * @return {Names}
	 */
	function getPathStore(node, path, destinationType, query) {
		// user defaults are applied if type of node at end of path is given
		const search = destinationType ? csiStore.searchByType(destinationType) : new Search();
		search.setLimitPath(node.$URL, path);

		if (query) {
			search.getQuery().importQuery(query);
		}

		return new Names({
			request: search.getRequest(),
			typeNode: destinationType
		});
	}

	/**
	 * Return store to search for nodes relative to the given context node (which is normally the node's parent)
	 * @param {C8Node} node
	 * @param {C8Node} contextNode
	 * @param {boolean} filterBySubtype
	 * @return {Names}
	 */
	function getSiblingStore(node, contextNode, filterBySubtype) {
		let rcr = reflection.getRelationBetweenNodes(node, contextNode, true);
		let path;
		let searchType;

		if (rcr) {
			// only allow searches if multiplicity is 0..* for relation
			if (!xml.attributeTypeIsContainer(rcr.getType())) {
				return null;
			}

			// if relation leads to multiple types of nodes (is abstract), then do add filter by type
			const rcDest = rcr.getDestinationClass(reflection.getNodeClass(contextNode));
			searchType = rcDest && rcDest.getType();
			path = xml.path(rcr.getId());
		} else {
			// determine if relation is in other direction
			rcr = reflection.getRelationBetweenNodes(contextNode, node);

			if (rcr) {
				path = xml.path(xml.PATH_OP_PARENT, rcr.getId());
				searchType = node.$Type;
			} else {
				return null;
			}
		}

		let query;

		// C8-49808: section of Data Setup does have a subtype specified, which is why filterBySubtype is true
		// however, the SpecificationSection class (a child of SpecificationSectionDefinition) does not use subtypes, 
		// so it does not make sense to filter by a subtype that doesn’t exist.
		if (filterBySubtype && xml.isValidURL(node.Subtype)) {
			query = new Query();
			query.RefAttr(reflection.ATTR_SUBTYPE, xml.OP_EQUALS, node.Subtype);
		}

		return getPathStore(contextNode, path, searchType, query);
	}

	function getFirstSearchType() {
		// summary: return type for first active search type
		var store = getTypeStore();
		var item = store.query(_activeStoreQuery)[0];
		return item && store.getIdentity(item);
	}

	function getImageHeading(/*String*/type, /*String?*/path) {
		// summary: return image heading used to display image in results
		var h = new Heading();
		h.m_name = "NodeImage";
		h.classRenderer = NodeImage;
		_resolveTypeHeading(type, h);
		h.m_path = path;
		return h;
	}

	function getNameHeading(/*String*/type, /*String?*/path) {
		// summary: return name heading used to display image in results
		var h = new Heading();
		h.m_prop = xml.PROP_NAME;
		_resolveTypeHeading(type, h);
		h.m_path = path;
		return h;
	}

	function getSearchHeadings(/*String*/type) {
		// summary: return configured headings for search
		return _getTypeHeadings(type);
	}

	function getSearchQueryPaths(/*String*/type) {
		// summary: get all paths necessary for search query
		var card = def.getSearchCard(type);
		return card ? card.getQueryPaths(reflection.getClass(type)) : null;
	}

	function getSearchType(/*String*/type) {
		// summary: get search configuration for given type
		var store = getTypeStore();
		var item = store && store.get(type);
		return item ? item.st : null;
	}

	/**
	 * Return query containing sort conditions for global search of given type
	 * @param {string} type
	 * @param {boolean} [bOnlyIfCustom] - only return a query if it contains custom sorts rather than standard sorting by name
	 * @param {Query} [query]
	 * @return {Query}
	 */
	function getSearchTypeSortQuery(type, bOnlyIfCustom, query) {
		var displayFormat = locale.getDisplayFormat(type);
		var st = getSearchType(type);
		var sorts = st && st.sorts;
		query = query || new Query();

		if (displayFormat) {
			query.SortByAttr(displayFormat.a1);
			query.SortByAttr(displayFormat.a2);
		} else if (sorts || !bOnlyIfCustom) {
			query.SortByNode(xml.PROP_NAME);
		}

		processSorts(sorts, query);

		return (bOnlyIfCustom && query.isEmpty()) ? null : query;
	}

	/**
	 * Return query for sorting global nodes of given type if declared; otherwise return null and they will just be sorted by name
	 * @param {string} type
	 * @param {Query} [query]
	 * @return {Query}
	 */
	function getSearchTypeQuery(type, query) {
		var st = getSearchType(type);
		if (st && st.filters) {
			query = query || new Query();
			query.importElements(st.filters);
		}

		return query;
	}

	function getTypeItems() {
		// returns search node types
		var tags = def.getSearchTagsByType();
		var items = [];

		_.each(tags, function(tag, type) {
			var rc = reflection.getClass(type);
			if (rc && !rc.abstractOrDisabled && def.canNavigateToType(type)) {
				items.push({
					id: type,
					name: locale.type(type),
					st: new SearchType(tag, type)
				});
			}
		});

		//sort the items before adding enterprise search
		items = csi.sort(items, "name");

		return items;
	}

	function getTypeStore() {
		// store node types for global search - allow NodeRef tags to override previous definitions
		// summary: returns sorted array of types that are configured to be searched for globally
		if (_typeStore === undefined) {
			var items = getTypeItems();
			if (items.length) {
				// this is to remove items from search options based on search options settings as per each role.
				_.each(items, function(item) {
					item.active = !configPreferences.isSearchTypeHidden(item.id);
				});

				_typeStore = new Memory({
					data: items
				});
			} else {
				_typeStore = null;
			}
		}

		return _typeStore;
	}

	/**
	 * Fetch the store of a particular attribute based on the configured search context
	 * @param {Spec} spec - the spec of this attribute
	 * @param {boolean} bBlanks - set to true to add options for blanks
	 * @return {Values}
	 */
	function getSpecAttributeValueStore(spec, bBlanks) {
		const type = spec.getSourceClass().getType();
		let query = new Query().Type(type);
		query = getSearchTypeQuery(type, query);

		return new Values({
			bBlanks,
			filterQuery: query,
			spec,
		});
	}

	function getTypeStoreQuery() {
		// summary: return query necessary to query for only active types
		return _activeStoreQuery;
	}

	/**
	 * Generate card definition
	 * @param {string} type - type of the node(favorite node or the search node)
	 * @param {Object} [node] - current node if of different type than the given type
	 * @param {string} [prefixPath] - the path from result type to node of given type
	 * @param {boolean} [bOmitDesc] - omit description heading if true
	 * @param {boolean} [bOmitType] - omit type heading if true
	 * @return {Object} card definition object
	 */
	function getCardItemDefs(type, node, prefixPath, bOmitDesc, bOmitType) {
		const nameHeading = lang.mixin(getNameHeading(type, prefixPath), {
			m_class: "csi-card-name-section"
		});

		const statusHeading = lang.mixin(new Heading(), {
			classRenderer: StatusRenderer
		});
		statusHeading.resolveHeading(locale.localeResolver);

		let descHeading;
		let typeHeading;

		if (!bOmitDesc) {
			descHeading = lang.mixin(new Heading(), {
				m_id: "Description",
				m_right: xml.RIGHT_NONE
			});
			descHeading.resolveHeading(locale.localeResolver);
		}

		if (!bOmitType) {
			typeHeading = lang.mixin(new Heading(), {
				m_id: xml.ATTR_TYPE,
				m_path: prefixPath,
				m_class: "csi-card-pill csi-card-type-section"
			});
			typeHeading.resolveHeading(locale.localeResolver);
		}

		// get configured headings per card for type
		const childNode = rootCache.getPathNode(node, prefixPath);
		const searchType = childNode?.$Type || type;
		const searchHeadings = getSearchHeadings(searchType)?.map((sh) => {
			const h = lang.mixin(new Heading(), {
				m_bAugmentable: false,
				m_id: sh.m_id,
				m_name: sh.m_name,
				m_path: sh.m_path,
			});
			_resolveTypeHeading(searchType, h);

			// apply prefix to path
			// TODO: Use SpecBuilder with path hints so that full path is used from start rather than set after the fact
			h.m_path = pathCreate(prefixPath, h.m_path);
			return h;
		});

		return {
			Image: getImageHeading(type, prefixPath),
			Name: nameHeading,
			Type: typeHeading,
			Desc: descHeading,
			Status: statusHeading,
			"*": searchHeadings
		};
	}

	return {
		getCardItemDefs,
		getFirstSearchType,
		getGlobalStore,
		getImageHeading,
		getNameHeading,
		getPathStore,
		getSearchHeadings,
		getSearchQueryPaths,
		getSearchType,
		getSearchTypeQuery,
		getSearchTypeSortQuery,
		getSiblingStore,
		getSpecAttributeValueStore,
		getTypeItems,
		getTypeStore,
		getTypeStoreQuery,
	};
});
